/**
 * Flight Thank You Slice
 *
 * Manages flight thank you (booking confirmation) state including:
 * - Thank you data loading states
 * - Booking confirmation data
 * - Polling state
 */
import { createSlice } from '@reduxjs/toolkit';
import type { FlightThankYouResponse } from '@contracts';
import { fetchFlightThankYouThunk, pollFlightThankYouThunk } from '../thunks/flightThankYouThunk';

// ============================================
// State Types
// ============================================

export interface FlightThankYouState {
  // Loading state
  isLoading: boolean;
  // Polling state
  isPolling: boolean;
  // Error state
  error: string | null;
  // Thank you data
  data: FlightThankYouResponse | null;
}

// ============================================
// Initial State
// ============================================

export const initialFlightThankYouState: FlightThankYouState = {
  isLoading: false,
  isPolling: false,
  error: null,
  data: null,
};

// ============================================
// Slice
// ============================================

const flightThankYouSlice = createSlice({
  name: 'flightThankYou',
  initialState: initialFlightThankYouState,
  reducers: {
    resetFlightThankYouState: () => initialFlightThankYouState,
    clearFlightThankYouError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Flight Thank You
    builder
      .addCase(fetchFlightThankYouThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFlightThankYouThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
      })
      .addCase(fetchFlightThankYouThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Failed to fetch flight booking confirmation';
      });

    // Poll Flight Thank You
    builder
      .addCase(pollFlightThankYouThunk.pending, (state) => {
        state.isPolling = true;
        state.error = null;
      })
      .addCase(pollFlightThankYouThunk.fulfilled, (state) => {
        state.isPolling = false;
      })
      .addCase(pollFlightThankYouThunk.rejected, (state, action) => {
        state.isPolling = false;
        state.error = action.payload ?? 'Failed to poll booking confirmation';
      });
  },
});

// ============================================
// Exports
// ============================================

export const { resetFlightThankYouState, clearFlightThankYouError } = flightThankYouSlice.actions;
export const flightThankYouReducer = flightThankYouSlice.reducer;
